import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useLocation() {
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('unknown');

  const getHeaders = () => {
    const userId = localStorage.getItem('userId');
    return userId ? { 'X-User-Id': userId } : {};
  };

  useEffect(() => {
    // Check permission status
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionStatus(result.state);
        result.onchange = () => setPermissionStatus(result.state);
      });
    }
  }, []);

  const requestLocation = async () => {
    if (!navigator.geolocation) {
      setLocationError('المتصفح لا يدعم تحديد الموقع');
      return null;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setLocation(coords);
          setLocationError(null);
          setPermissionStatus('granted');

          // Save to server
          try {
            await axios.post(`${BACKEND_URL}/api/profile/location`, coords, {
              withCredentials: true,
              headers: getHeaders()
            });
          } catch (err) {
            console.error('Error saving location:', err);
          }

          resolve(coords);
        },
        (error) => {
          let errorMessage = 'حدث خطأ في تحديد الموقع';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'تم رفض إذن تحديد الموقع';
              setPermissionStatus('denied');
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'الموقع غير متاح';
              break;
            case error.TIMEOUT:
              errorMessage = 'انتهت مهلة تحديد الموقع';
              break;
            default:
              break;
          }
          setLocationError(errorMessage);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  return {
    location,
    locationError,
    permissionStatus,
    requestLocation
  };
}

// Component to display distance
export function DistanceBadge({ distance }) {
  if (!distance || distance.distance_km === null) {
    return null;
  }

  const formatDistance = (km) => {
    if (km === 0) return 'نفس المدينة';
    if (km < 1) return `${Math.round(km * 1000)} متر`;
    if (km < 10) return `${km.toFixed(1)} كم`;
    return `${Math.round(km)} كم`;
  };

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      background: distance.type === 'دقيق' ? 'rgba(0, 137, 123, 0.15)' : 'rgba(255, 152, 0, 0.15)',
      color: distance.type === 'دقيق' ? '#00897B' : '#F57C00',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500'
    }}>
      <span>📍</span>
      <span>{formatDistance(distance.distance_km)}</span>
      {distance.type === 'تقريبي' && (
        <span style={{ fontSize: '10px', opacity: 0.8 }}>(تقريبي)</span>
      )}
    </div>
  );
}

// Banner to request location permission
export function LocationPermissionBanner({ onLocationGranted }) {
  const { permissionStatus, requestLocation, locationError } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem('locationBannerDismissed');
    if (wasDismissed) setDismissed(true);
  }, []);

  if (permissionStatus === 'granted' || permissionStatus === 'denied' || dismissed) {
    return null;
  }

  const handleRequest = async () => {
    const coords = await requestLocation();
    if (coords && onLocationGranted) {
      onLocationGranted(coords);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('locationBannerDismissed', 'true');
    setDismissed(true);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #FF9800, #FFC107)',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      color: 'white'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '24px' }}>📍</span>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: '600' }}>
            فعّل تحديد الموقع
          </h4>
          <p style={{ margin: 0, fontSize: '12px', opacity: 0.9 }}>
            لمعرفة المسافة الدقيقة بينك وبين المستخدمين الآخرين
          </p>
          {locationError && (
            <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#FFEBEE' }}>
              {locationError}
            </p>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button
          onClick={handleRequest}
          style={{
            flex: 1,
            padding: '8px 16px',
            background: 'white',
            color: '#F57C00',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          تفعيل
        </button>
        <button
          onClick={handleDismiss}
          style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          لاحقاً
        </button>
      </div>
    </div>
  );
}

export default { useLocation, DistanceBadge, LocationPermissionBanner };
